import React from 'react';
import { TIME_SLOT_WIDTH } from '../../utils/timeSlotUtils';

/**
 * 共通時間軸ヘッダーのプロパティ
 */
export interface SharedTimeAxisHeaderProps {
  /**
   * 表示する時間スロット配列（HH:mm形式）
   */
  timeSlots: string[];

  /**
   * 1時間スロットの幅（px）
   * @default 80
   */
  timeSlotWidth?: number;
}

/**
 * 共通時間軸ヘッダーコンポーネント
 * FL-158: 陸送員スケジュールのレイアウト最適化
 *
 * 全陸送員で共有する時間軸をページ上部に固定表示します。
 * スクロール時もヘッダーは固定され、常に時間軸が視認可能です。
 *
 * @example
 * ```tsx
 * <SharedTimeAxisHeader
 *   timeSlots={['08:00', '09:00', '10:00', ...]}
 * />
 * ```
 */
export const SharedTimeAxisHeader: React.FC<SharedTimeAxisHeaderProps> = ({
  timeSlots,
  timeSlotWidth = TIME_SLOT_WIDTH,
}) => {
  return (
    <div className='sticky top-0 z-20 bg-white dark:bg-gray-800 border-b-2 border-gray-300 dark:border-gray-600 shadow-sm'>
      <div className='flex' role='row' aria-label='時間軸ヘッダー'>
        {/* 左側: 陸送員名エリア用スペーサー */}
        <div
          className='w-32 sm:w-48 flex-shrink-0 bg-gray-100 dark:bg-gray-700 border-r-2 border-gray-300 dark:border-gray-600 sticky left-0 z-30'
          role='columnheader'
        >
          <div className='px-3 sm:px-4 py-3 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300'>
            陸送員
          </div>
        </div>

        {/* 右側: 時間軸 */}
        <div className='flex-1'>
          <div
            className='flex'
            data-testid='time-axis-container'
            style={{ width: `${timeSlots.length * timeSlotWidth}px` }}
          >
            {timeSlots.map(timeSlot => (
              <div
                key={timeSlot}
                role='columnheader'
                className='border-r border-gray-200 dark:border-gray-700 last:border-r-0'
                style={{
                  width: `${timeSlotWidth}px`,
                  flexShrink: 0,
                }}
              >
                <div className='px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400'>
                  {timeSlot}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
